/**
 * useDocumentAttach
 *
 * Owns the document-attachment / preview modal state that was previously
 * class state on FormViewer:
 *
 *   openAddAttachmentModal()            — open the attach-document modal
 *   closeAddAttachmentModal()           — close the attach-document modal
 *   showPreview(attachment)             — show the preview modal for an attachment
 *   closePreviewModal()                 — close the preview modal
 *   openRequestPDFModal()               — show the PDF-alert dialog
 *   closeRequestPDFModal()              — close the PDF-alert dialog
 *   toggleRequestDocsModal()            — toggle the request-documents modal
 *   onClickDeleteDocument(docID)        — delete via calc engine
 *   onClickDetachDocument(docID, var)   — detach via calc engine
 *   getLatestArchivedDocuments()        — refresh from API
 *
 * State returned for render:
 *   addAttachmentModalOpen
 *   currentOpenedAttachment
 *   isPreviewModalOpen
 *   isPDFAlertModalOpen
 *   isRequestDocumentModalOpen
 *   preparerID / setPreparerID
 */
import { useState, useCallback } from 'react';
import XlinkAPI from '~/app/api/xlinkAPI';

const useDocumentAttach = ({
  calcAPIRef,
  returnIDRef,
  onSetUpdatedAttachmentList,
  onShowErrorDialog,
}) => {
  const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState(false);
  const [currentOpenedAttachment, setCurrentOpenedAttachment] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isPDFAlertModalOpen, setIsPDFAlertModalOpen] = useState(false);
  const [isRequestDocumentModalOpen, setIsRequestDocumentModalOpen] = useState(false);
  const [preparerID, setPreparerID] = useState(0);

  const openAddAttachmentModal = useCallback(() => setAddAttachmentModalOpen(true), []);
  const closeAddAttachmentModal = useCallback(() => setAddAttachmentModalOpen(false), []);

  const showPreview = useCallback(attachment => {
    setCurrentOpenedAttachment(attachment);
    setIsPreviewModalOpen(true);
  }, []);

  const closePreviewModal = useCallback(() => setIsPreviewModalOpen(false), []);
  const openRequestPDFModal = useCallback(() => setIsPDFAlertModalOpen(true), []);
  const closeRequestPDFModal = useCallback(() => setIsPDFAlertModalOpen(false), []);
  const toggleRequestDocsModal = useCallback(() => setIsRequestDocumentModalOpen(v => !v), []);

  const onClickDeleteDocument = useCallback(
    docID => {
      calcAPIRef.current?.ReturnRemoveDocument(docID);
    },
    [calcAPIRef],
  );

  const onClickDetachDocument = useCallback(
    (docID, attachVar) => {
      calcAPIRef.current?.ReturnDetachDocument(docID, attachVar);
    },
    [calcAPIRef],
  );

  const getLatestArchivedDocuments = useCallback(() => {
    XlinkAPI.getReturnProfileByReturnID(returnIDRef.current)
      .then(res => {
        let arr = [];
        if (res?.data?.preparer_id) {
          setPreparerID(res.data.preparer_id);
        }
        if (res?.data?.attachments) {
          arr = res.data.attachments;
        }
        onSetUpdatedAttachmentList(arr);
      })
      .catch(() => {
        onShowErrorDialog('Failed to update attachments list');
      });
  }, [returnIDRef, onSetUpdatedAttachmentList, onShowErrorDialog]);

  return {
    addAttachmentModalOpen,
    currentOpenedAttachment,
    setCurrentOpenedAttachment,
    isPreviewModalOpen,
    isPDFAlertModalOpen,
    isRequestDocumentModalOpen,
    preparerID,
    setPreparerID,
    openAddAttachmentModal,
    closeAddAttachmentModal,
    showPreview,
    closePreviewModal,
    openRequestPDFModal,
    closeRequestPDFModal,
    toggleRequestDocsModal,
    onClickDeleteDocument,
    onClickDetachDocument,
    getLatestArchivedDocuments,
  };
};

export default useDocumentAttach;
